import React, { useState, useEffect, forwardRef, useCallback } from 'react'
import {
  Card,
  Text,
  Stack,
  Flex,
  Box,
  Heading,
  TextInput,
  Spinner,
  Label,
} from '@sanity/ui'
import { set, setIfMissing } from 'sanity'
import { useClient } from 'sanity'
import { ArrayOfObjectsInputProps, useFormValue } from 'sanity'

interface ScoringCriteria {
  _id: string
  name: string
}

interface Penalty {
  name: string
  penalty: number
  type: string
}

interface EaseOfFitDetails {
  score: number
  penalties: Penalty[]
  message?: string
}

// Tire Availability criteria ID - scores are calculated via the Utilities tab
const TIRE_AVAILABILITY_CRITERIA_ID = '590519c8-4ec7-4dfc-8642-09c2c6a8b64f'

interface ScoreValue {
  _key?: string
  scoreCriteria: {
    _type: 'reference'
    _ref: string
  }
  score: number
}

const ScoringInput = forwardRef<HTMLDivElement, ArrayOfObjectsInputProps>(
  (props, ref) => {
    const { value, readOnly, onChange, schemaType } = props

    // Get document and parent from form context
    const document = useFormValue([]) as any
    const parent = useFormValue([...(props.path || []).slice(0, -1)]) as any

    const [scoringCriteria, setScoringCriteria] = useState<ScoringCriteria[]>([])
    const [scores, setScores] = useState<Record<string, number>>({})
    const [maxScore, setMaxScore] = useState(100)
    const [isLoadingScoringCriteria, setIsLoadingScoringCriteria] = useState(true)
    const [isLoadingMaxScore, setIsLoadingMaxScore] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [easeOfFitDetails, setEaseOfFitDetails] = useState<EaseOfFitDetails | null>(
      null,
    )
    const [easeOfFitCriteriaId, setEaseOfFitCriteriaId] = useState<string | null>(null)
    const [performanceCriteriaId, setPerformanceCriteriaId] = useState<string | null>(null)

    // Tire Availability is a *relative* score - each fitment is graded against the
    // best-served fitment in the same guide. A guide with a single listing has no
    // benchmark, so the frontend awards a perfect score at read time. Mirror that here
    // instead of showing an empty "—" the editor cannot fill via the Utilities tab.
    const isSingleFitmentGuide =
      (document?.fitmentTypes || []).reduce(
        (sum: number, ft: any) => sum + (ft?.listingItems?.length || 0),
        0,
      ) === 1

    const client = useClient({ apiVersion: '2023-05-03' })

    // Fetch max score and scoring criteria
    useEffect(() => {
      let mounted = true

      const fetchMaxScore = async () => {
        try {
          setIsLoadingMaxScore(true)
          const query = `*[_type == 'buyerGuidesTemplate'][0].scoringMaxResult`
          const result = await client.fetch(query)
          if (mounted && result) {
            setMaxScore(result)
          }
        } catch (err) {
          if (mounted) {
            console.error('Error fetching max score:', err)
          }
        } finally {
          if (mounted) {
            setIsLoadingMaxScore(false)
          }
        }
      }

      const fetchScoringCriteria = async () => {
        try {
          setIsLoadingScoringCriteria(true)
          const query = `*[_type == 'scoringCriteria']{ _id, name }`
          const result = await client.fetch<ScoringCriteria[]>(query)
          if (mounted) {
            setScoringCriteria(result.sort((a, b) => a.name.localeCompare(b.name)))
          }
        } catch (err) {
          if (mounted) {
            setError('Failed to load Scoring Criteria')
            console.error('Error fetching Scoring Criteria:', err)
          }
        } finally {
          if (mounted) {
            setIsLoadingScoringCriteria(false)
          }
        }
      }

      fetchMaxScore()
      fetchScoringCriteria()

      return () => {
        mounted = false
      }
    }, [client])

    // Calculate Ease of fit score based on fitment tags
    useEffect(() => {
      const calculateEaseOfFit = async () => {
        if (!easeOfFitCriteriaId) {
          return
        }

        if (!document && !parent) {
          return
        }

        try {
          let fitmentData: any = null

          // Helper function to check if a value is a valid reference (not empty)
          const hasActualReference = (val: unknown): boolean => {
            if (val == null) return false
            if (typeof val === 'string') return val.trim().length > 0
            if (typeof val === 'object' && '_ref' in val) {
              const ref = (val as { _ref?: string })._ref
              return typeof ref === 'string' && ref.trim().length > 0
            }
            return false
          }

          // Prefer current listing (parent) - we're editing its scoringValues
          // Check if parent has the structure of a listing item
          if (
            parent &&
            (parent.content?.tags?.length > 0 ||
              parent.content?.extraFitmentNotes?.length > 0 ||
              parent.tireDetails)
          ) {
            fitmentData = parent
          } else if (document && document.fitmentTypes) {
            // Fallback: first listing with tags from any fitment type
            for (const fitmentType of document.fitmentTypes) {
              if (fitmentType.listingItems) {
                for (const item of fitmentType.listingItems) {
                  if (item.content && item.content.tags && item.content.tags.length > 0) {
                    fitmentData = item
                    break
                  }
                }
              }
              if (fitmentData) break
            }
          }

          if (!fitmentData && document && document.content && document.content.tags) {
            fitmentData = document
          }

          const tags = fitmentData?.content?.tags || []
          const tagRefs = tags.map((tag: any) => tag._ref).filter(Boolean)
          const extraNotes = fitmentData?.content?.extraFitmentNotes || []
          const hasExtraNotePenalties = extraNotes.some(
            (note: any) => (note.penaltyScore || 0) > 0,
          )

          // Check if there are spacers or cambers that might have penalties
          const hasSpacers =
            hasActualReference(fitmentData?.tireDetails?.spacers?.spacerFront) ||
            hasActualReference(fitmentData?.tireDetails?.spacers?.spacerRear)
          const hasCambers =
            hasActualReference(fitmentData?.tireDetails?.cambers?.camberFront) ||
            hasActualReference(fitmentData?.tireDetails?.cambers?.camberRear)

          // Fetch spacersTag and cambersTag first to get their IDs for exclusion
          const [spacersTag, cambersTag] = await Promise.all([
            client.fetch(`*[_type == 'spacersTag'][0]{ _id, penaltyScore, displayName }`),
            client.fetch(`*[_type == 'cambersTag'][0]{ _id, penaltyScore, displayName }`),
          ])

          // Only return early if there are no tags, no extra notes, and no spacer/camber penalties
          const hasSpacerPenalty = hasSpacers && spacersTag?.penaltyScore
          const hasCamberPenalty = hasCambers && cambersTag?.penaltyScore
          if (tagRefs.length === 0 && !hasExtraNotePenalties && !hasSpacerPenalty && !hasCamberPenalty) {
            setEaseOfFitDetails({ score: 10, penalties: [] })
            return
          }

          // Auto-tag IDs that should be excluded from manual tag penalties
          // These are calculated automatically and shouldn't be penalized as regular tags
          const autoTagIds = [
            'forgedTag',
            'flowFormedTag',
            'squareTag',
            'rotateableTag',
            'staggeredTag',
            'doubleStaggeredTag',
            'reverseStaggeredTag',
            'dualOffsetTag',
            spacersTag?._id,
            cambersTag?._id,
          ].filter(Boolean)

          let tagDetails: any[] = []
          if (tagRefs.length > 0) {
            const query = `*[_type == 'fitmentTag' && _id in $tagRefs]{
              _id,
              name,
              displayName,
              penaltyScore
            }`
            tagDetails = await client.fetch(query, { tagRefs })
          }

          let totalPenalty = 0
          const penalties: Penalty[] = []

          // Process manual tags (excluding auto-tags and spacer/camber tags)
          tagDetails.forEach((tag) => {
            // Skip auto-tags and spacer/camber tags - these are penalized separately
            if (autoTagIds.includes(tag._id)) {
              return
            }
            const penalty = tag.penaltyScore || 0
            if (penalty > 0) {
              totalPenalty += penalty
              penalties.push({
                name: tag.displayName || tag.name,
                penalty: penalty,
                type: 'tag',
              })
            }
          })

          // Process extra fitment notes
          extraNotes.forEach((note: any, index: number) => {
            const penalty = note.penaltyScore || 0
            if (penalty > 0) {
              let noteText = `Extra note ${index + 1}`
              if (note.content && note.content.length > 0) {
                const firstBlock = note.content[0]
                if (firstBlock.children && firstBlock.children.length > 0) {
                  noteText = firstBlock.children
                    .filter((child: any) => child.text)
                    .map((child: any) => child.text)
                    .join(' ')
                    .substring(0, 30)
                  if (noteText.length === 30) {
                    noteText += '...'
                  }
                }
              }

              totalPenalty += penalty
              penalties.push({
                name: noteText,
                penalty: penalty,
                type: 'extraNote',
              })
            }
          })

          // Add spacer penalty if spacers are actually present in tireDetails
          if (hasSpacerPenalty) {
            totalPenalty += spacersTag.penaltyScore
            penalties.push({
              name: spacersTag.displayName || 'Spacers',
              penalty: spacersTag.penaltyScore,
              type: 'spacers',
            })
          }

          // Add camber penalty if cambers are actually present in tireDetails
          if (hasCamberPenalty) {
            totalPenalty += cambersTag.penaltyScore
            penalties.push({
              name: cambersTag.displayName || 'Cambers',
              penalty: cambersTag.penaltyScore,
              type: 'cambers',
            })
          }

          const calculatedScore = Math.max(0, 10 - totalPenalty)
          setEaseOfFitDetails({
            score: calculatedScore,
            penalties: penalties,
          })
        } catch (err) {
          console.error('Error calculating Ease of fit:', err)
        }
      }

      calculateEaseOfFit()
    }, [document, easeOfFitCriteriaId, parent, client])

    // Find the Ease of fit and Performance criteria IDs
    useEffect(() => {
      const easeOfFit = scoringCriteria.find((c) => c.name === 'Ease of fit')
      if (easeOfFit) {
        setEaseOfFitCriteriaId(easeOfFit._id)
      }

      const performance = scoringCriteria.find((c) => c.name === 'Performance')
      if (performance) {
        setPerformanceCriteriaId(performance._id)
      }
    }, [scoringCriteria])

    const isLoading = isLoadingScoringCriteria || isLoadingMaxScore

    // Convert array to map format
    const convertArrayToMap = (arr: ScoreValue[]) => {
      const map: Record<string, number> = {}
      if (!arr || !Array.isArray(arr)) return map

      arr.forEach((item) => {
        if (!item.scoreCriteria?._ref || item.score === undefined) return
        const criteriaId = item.scoreCriteria._ref
        map[criteriaId] = item.score
      })

      return map
    }

    // Convert map back to array format
    const convertMapToArray = (map: Record<string, number>): ScoreValue[] => {
      return Object.keys(map).map((criteriaId) => ({
        scoreCriteria: {
          _type: 'reference',
          _ref: criteriaId,
        },
        score: map[criteriaId],
      }))
    }

    // Initialize scores from value
    useEffect(() => {
      if (value && Array.isArray(value)) {
        // Map value items to ScoreValue format
        const validScores: ScoreValue[] = value
          .filter((item: any) => {
            return (
              item &&
              typeof item === 'object' &&
              'scoreCriteria' in item &&
              'score' in item &&
              item.scoreCriteria &&
              typeof item.scoreCriteria === 'object' &&
              '_ref' in item.scoreCriteria
            )
          })
          .map((item: any) => ({
            _key: item._key,
            scoreCriteria: item.scoreCriteria,
            score: item.score,
          }))
        setScores(convertArrayToMap(validScores))
      } else if (!value) {
        onChange(setIfMissing([]))
      }
    }, [value, onChange])

    // Handle score change
    const handleScoreChange = useCallback(
      (criteriaId: string, newValue: string) => {
        const updatedScores = { ...scores }

        if (newValue === '') {
          delete updatedScores[criteriaId]
        } else {
          const numValue = parseFloat(newValue)
          if (!isNaN(numValue)) {
            updatedScores[criteriaId] = numValue
          }
        }

        setScores(updatedScores)
        const scoresArray = convertMapToArray(updatedScores)
        onChange(set(scoresArray))
      },
      [scores, onChange],
    )

    // Get current score
    const getCurrentScore = (criteriaId: string) => {
      if (scores[criteriaId] === undefined || scores[criteriaId] === null) {
        return ''
      }
      return scores[criteriaId].toString()
    }

    return (
      <Stack gap={3} ref={ref}>
        <Box>
          <Label size={1}>{schemaType.title}</Label>
          {schemaType.description && (
            <Box paddingTop={2}>
              <Label size={0} muted>
                {schemaType.description}
              </Label>
            </Box>
          )}
        </Box>

        {isLoading ? (
          <Flex align="center" justify="center" padding={4}>
            <Spinner muted />
            <Box marginLeft={3}>
              <Text>Loading data...</Text>
            </Box>
          </Flex>
        ) : error ? (
          <Card tone="critical" padding={4}>
            <Text>{error}</Text>
          </Card>
        ) : (
          <Card padding={4} radius={2} shadow={1}>
            <Stack gap={4}>
              <Heading size={1}>Scoring Configuration</Heading>
              <Text size={1} muted>
                Configure scores for each criteria. Maximum score: {maxScore}
              </Text>

              <Box overflow="auto">
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr>
                      <th
                        style={{
                          padding: '8px',
                          borderBottom: '1px solid #ccc',
                          textAlign: 'left',
                          width: '70%',
                        }}
                      >
                        Scoring Criteria
                      </th>
                      <th
                        style={{
                          padding: '8px',
                          borderBottom: '1px solid #ccc',
                          textAlign: 'center',
                          width: '30%',
                        }}
                      >
                        Score
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {scoringCriteria.map((criteria) => (
                      <tr key={criteria._id}>
                        <td
                          style={{
                            padding: '8px',
                            borderBottom: '1px solid #eee',
                            fontWeight: 'bold',
                          }}
                        >
                          {criteria.name}
                          {criteria._id === easeOfFitCriteriaId && easeOfFitDetails && (
                            <div
                              style={{
                                fontSize: '12px',
                                fontWeight: 'normal',
                                marginTop: '4px',
                              }}
                            >
                              <span style={{ color: '#666' }}>
                                {easeOfFitDetails.message ||
                                  'Auto-calculated based on fitment tags and extra notes'}
                              </span>
                              {easeOfFitDetails.penalties &&
                                easeOfFitDetails.penalties.length > 0 && (
                                  <div style={{ marginTop: '4px' }}>
                                    <strong>Penalties:</strong>
                                    {easeOfFitDetails.penalties.map((p, idx) => (
                                      <div
                                        key={idx}
                                        style={{ fontSize: '11px', marginLeft: '8px' }}
                                      >
                                        • {p.name}: -{p.penalty}
                                      </div>
                                    ))}
                                  </div>
                                )}
                            </div>
                          )}
                          {criteria._id === TIRE_AVAILABILITY_CRITERIA_ID &&
                            isSingleFitmentGuide && (
                              <div
                                style={{
                                  fontSize: '12px',
                                  fontWeight: 'normal',
                                  marginTop: '4px',
                                  color: '#666',
                                }}
                              >
                                Single fitment — no other fitment to compare against, so
                                this scores {maxScore} automatically
                              </div>
                            )}
                          {((criteria._id === TIRE_AVAILABILITY_CRITERIA_ID &&
                            !isSingleFitmentGuide) ||
                            criteria._id === performanceCriteriaId) && (
                            <div
                              style={{
                                fontSize: '12px',
                                fontWeight: 'normal',
                                marginTop: '4px',
                                color: '#666',
                              }}
                            >
                              Calculate scores via the Utilities tab on the Buyer Guide
                            </div>
                          )}
                        </td>
                        <td
                          style={{
                            padding: '8px',
                            borderBottom: '1px solid #eee',
                            textAlign: 'center',
                          }}
                        >
                          {criteria._id === easeOfFitCriteriaId &&
                          easeOfFitDetails &&
                          easeOfFitDetails.score !== null ? (
                            <div style={{ padding: '6px 0' }}>
                              <strong>{easeOfFitDetails.score.toFixed(1)}</strong>
                              <div style={{ fontSize: '11px', color: '#666' }}>
                                (auto-calculated)
                              </div>
                            </div>
                          ) : criteria._id === TIRE_AVAILABILITY_CRITERIA_ID &&
                            isSingleFitmentGuide ? (
                            <div style={{ padding: '6px 0' }}>
                              <strong>{maxScore.toFixed(1)}</strong>
                              <div style={{ fontSize: '11px', color: '#666' }}>
                                (auto-calculated)
                              </div>
                            </div>
                          ) : criteria._id === TIRE_AVAILABILITY_CRITERIA_ID ||
                            criteria._id === performanceCriteriaId ? (
                            <div style={{ padding: '6px 0' }}>
                              <strong>
                                {scores[criteria._id] !== undefined
                                  ? scores[criteria._id].toFixed(1)
                                  : '—'}
                              </strong>
                              <div style={{ fontSize: '11px', color: '#666' }}>
                                (from Utilities tab)
                              </div>
                            </div>
                          ) : (
                            <TextInput
                              type="number"
                              step="0.5"
                              placeholder="0.0"
                              max={maxScore}
                              min={0}
                              value={getCurrentScore(criteria._id)}
                              onChange={(event) =>
                                handleScoreChange(criteria._id, event.currentTarget.value)
                              }
                              disabled={readOnly}
                              style={{ width: '100px' }}
                            />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Box>
            </Stack>
          </Card>
        )}
      </Stack>
    )
  },
)

ScoringInput.displayName = 'ScoringInput'

export default ScoringInput
